import { useState, useEffect, ChangeEvent, Dispatch, SetStateAction } from "react";
import { Tooltip } from "react-tooltip";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Column } from "primereact/column";
import {
	DataTableExpandedRows,
	DataTableValueArray,
	DataTableRowEvent,
	DataTable,
} from "primereact/datatable";
import { CheckBadgeIcon, PlusIcon } from "@heroicons/react/24/solid";
import {
	CheckBadgeIcon as CheckBadgeIconOutline,
	ClockIcon,
	ExclamationTriangleIcon,
	TrashIcon,
	XCircleIcon,
} from "@heroicons/react/24/outline";
import { useAppSelector } from "../../app/hooks";
import ButtonWithConfirm from "../../components/ButtonWithConfirm";
import ModalFrame from "../../components/ModalFrame";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import { SimpleOption } from "../../components/input/SimpleListbox";
import NoDataTableBody from "../../components/NoDataTableBody";
import TextWithTip from "../../components/TextWithTip";
import RtkqResultViewer from "../../components/RtkqResultViewer";
import FormattedDate from "../../components/FormattedDate";
import Avatar from "../../components/Avatar";
import ProgressBar from "../../components/ProgressBar";
import SearchFilter from "../../components/input/SearchFilter";
import Select from "../../components/input/Select";
import SimplePagination from "../../components/SimplePagination";
import { SelectDevelopmentView } from "../../components/input/SelectDevelopmentView";
import {
	useGetTutorialsQuery,
	useGetCategoriesQuery,
	TutorialViewModel,
	useGetCourseActivityQuery,
	TutorialStudentAssignmentViewModel,
	useRemoveLessonAssignmentMutation,
	useRemoveAllLessonAssignmentsMutation,
} from "./lessonsApiSlice";
import { LessonStatus } from "./StudentLessons";
import AssignLessonForm from "./AssignLessonForm";

interface LessonData extends TutorialViewModel {
	categoryName: string;
	assignments: TutorialStudentAssignmentViewModel[];
	startedCount: number;
	incompleteCount: number;
	totalCount: number;
	status: LessonStatus | undefined;
}

export enum TeacherLessonsSubTabs {
	AssingedLessonActivity = "Assigned Lesson Activity",
	LessonLibrary = "Lesson Library",
}

const initialSubTabs = [
	{ name: TeacherLessonsSubTabs.AssingedLessonActivity, current: true },
	{ name: TeacherLessonsSubTabs.LessonLibrary, current: false },
];

export function TeacherLessons() {
	const config = useAppSelector((state) => state.site.config);

	const [isAssignLessonDialogOpen, setIsAssignLessonDialogOpen] = useState(false);
	const [lockLesson, setLockLesson] = useState(false);
	const [lessonSelected, setLessonSelected] = useState<number>();

	const [view, setView] = useState<string>("table");
	const [tabs, setTabs] = useState(initialSubTabs);
	const [currentTab, setCurrentTab] = useState(initialSubTabs[0].name);
	const [firstLoad, setFirstLoad] = useState(true);

	const [statusFilter, setStatusFilter] = useState("in progress");
	const [categoryOptions, setCategoryOptions] = useState<SimpleOption[]>([]);
	const [categoryFilter, setCategoryFilter] = useState("");
	const [difficultyFilter, setDifficultyFilter] = useState("");
	const [searchFilter, setSearchFilter] = useState<string>("");

	const [alertMessage, setAlertMessage] = useState("");

	const [lessonData, setLessonData] = useState<LessonData[]>([]);
	const [lessonActivity, setLessonActivity] = useState<LessonData[]>([]);
	const [filteredLessonActivity, setFilteredLessonActivity] = useState<LessonData[]>([]);

	const pageSize = 5;
	const [page, setPage] = useState<number>(0);
	const isLessonDataFiltered =
		categoryFilter !== "0" || difficultyFilter !== "" || searchFilter !== "";

	function getFilteredLessonData(items: LessonData[]): LessonData[] {
		return items
			.filter((row) => row.name.toLowerCase().includes(searchFilter.trim().toLowerCase()))
			.filter((row) => {
				if (categoryFilter === "0") return row;
				return row.categoryId.toString() == categoryFilter;
			})
			.filter((row) => difficultyFilter == "" || difficultyFilter == row.difficulty);
	}

	const getPagedData = (items: LessonData[]): LessonData[] => {
		const start = page * pageSize;
		return items.slice(start, start + pageSize);
	};

	const handlePageChange = (page: number) => {
		setPage(page);
	};

	const handleFilterChangeWithPagination = (
		setter: Dispatch<SetStateAction<string>>,
		value: string
	) => {
		setter(value);
		setPage(0);
	};

	const location = useLocation();
	const navigate = useNavigate();

	const tutsResult = useGetTutorialsQuery();
	const tutorials = tutsResult.data;

	const catsResult = useGetCategoriesQuery();
	const categories = catsResult.data;

	const actiResult = useGetCourseActivityQuery();
	const activity = actiResult.data;

	const isFetching = tutsResult.isFetching || catsResult.isFetching || actiResult.isFetching;
	const isError = tutsResult.isError || catsResult.isError || actiResult.isError;
	const isSuccess = tutsResult.isSuccess && catsResult.isSuccess && actiResult.isSuccess;

	useEffect(() => {
		if (location.state != null) {
			setLessonSelected(location.state.id);
			setIsAssignLessonDialogOpen(location.state.openDialog);
		}
		navigate(location, { replace: true });
	}, []);

	useEffect(() => {
		let options: SimpleOption[] = [];
		if (categories) {
			options = [...categories]
				.sort((a, b) => a.order - b.order)
				.map((c) => ({
					value: c.categoryId.toString() ?? "",
					label: c.name,
				}));

			options.unshift({
				value: 0,
				label: "All Categories",
			});
			setCategoryFilter(options[0].value.toString());
		}
		setCategoryOptions(options);
	}, [categories]);

	useEffect(() => {
		if (!tutorials || !activity || !config) return setLessonData([]);
		const lessons: LessonData[] = tutorials.map((t) => {
			const _assigned = activity.assignedLessons.find((l) => l.tutorialId == t.tutorialId);

			let _status = undefined;
			if (_assigned) {
				if (_assigned.completed) {
					_status = LessonStatus.Complete;
				} else {
					_status = LessonStatus.InProgress;
				}
			}

			const x: LessonData = {
				...t,
				categoryName: categories?.find((c) => c.categoryId == t.categoryId)?.name ?? "",
				assignments: _assigned?.studentAssignments ?? [],
				startedCount: _assigned?.startedCount ?? 0,
				incompleteCount: _assigned?.incompleteCount ?? 0,
				totalCount: _assigned?.totalCount ?? 0,
				status: _status,
			};

			return x;
		});
		setLessonData(lessons);
		setLessonActivity(lessons.filter((l) => l.status != undefined));
	}, [tutorials, activity]);

	useEffect(() => {
		setFilteredLessonActivity(
			lessonActivity.filter((row) => {
				switch (statusFilter) {
					case "complete":
						return row.status == LessonStatus.Complete;
					case "in progress":
						return row.status == LessonStatus.InProgress;
				}
				return true;
			})
		);
	}, [statusFilter, lessonActivity]);

	useEffect(() => {
		if (
			lessonSelected &&
			lessonActivity.find((l) => l.tutorialId === lessonSelected)?.status ==
				LessonStatus.Complete
		)
			setStatusFilter("all");
	}, [lessonActivity]);

	useEffect(() => {
		if (!activity) return;
		if (activity.assignedLessons.length === 0 && firstLoad) {
			handleSubTabChange(TeacherLessonsSubTabs.LessonLibrary);
		}
		setFirstLoad(false);
	}, [activity]);

	function handleSubTabChange(name: TeacherLessonsSubTabs): void {
		setTabs(
			tabs.map((tab) =>
				tab.name === name ? { ...tab, current: true } : { ...tab, current: false }
			)
		);
		setCurrentTab(name);
		setAlertMessage("");
		setSearchFilter("");
		setLessonSelected(undefined);
	}

	function handleOpenAssignLessonDialog(lockLesson: boolean, lessonSelected?: number) {
		setLockLesson(lockLesson);
		setLessonSelected(lessonSelected);
		setIsAssignLessonDialogOpen(true);
	}

	const handleLessonAssigned = () => {
		if (currentTab != TeacherLessonsSubTabs.AssingedLessonActivity) {
			handleSubTabChange(TeacherLessonsSubTabs.AssingedLessonActivity);
		}
	};

	return (
		<>
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Lessons</h1>
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
					options={[
						{ value: "table", label: "Tables" },
						{ value: "raw", label: "Raw Data" },
					]}
				/>
			</div>
			<p className="mt-4">
				Welcome to the Lessons page! From this page, you can view your Assigned Lesson
				Activity, which displays all lessons you have assigned to your students. You can
				click on a lesson in the list to view student progress, assign and remove students
				from the assignment. The Lesson Library contains all lessons listed by category.
				Click on the drop-down menus to view options. Audio is available, so get your
				headphones ready.
			</p>
			{view == "table" && (
				<div className="w-full">
					<div className="mt-8 px-4">
						<div
							aria-label="Activity Tabs"
							className="flex divide-x divide-gray-200 rounded-lg shadow-sm items-center">
							{tabs.map((tab, tabIdx) => (
								<button
									onClick={() => handleSubTabChange(tab.name)}
									key={tab.name}
									aria-current={tab.current ? "page" : undefined}
									className={`${
										tab.current
											? "text-gray-900 !text-lg bg-gray-100 border-b-2 border-b-[var(--subtab-border)]"
											: "text-gray-500 hover:text-gray-700 border-b !border-b-transparent hover:bg-gray-100"
									} 
									${tabIdx === 0 ? "rounded-l-lg" : tabIdx === tabs.length - 1 ? "rounded-r-lg" : ""} 
									group flex-1 relative p-4 text-center text-sm font-medium hover:cursor-pointer focus:z-10`}>
									<span>{tab.name}</span>
								</button>
							))}
						</div>
						<div className="overflow-hidden mx-auto max-w-none">
							<section className="mt-4">
								{isFetching && <p>Fetching lessons...</p>}
								{isError && <p>Error fetching lessons...</p>}
								{isSuccess &&
									currentTab === TeacherLessonsSubTabs.AssingedLessonActivity && (
										<section className="shadow-sm">
											{alertMessage && (
												<AlertMessage
													className="my-4 whitespace-pre-line"
													type={AlertLevel.Warning}
													message={alertMessage}
													showIcon={true}
													onClear={() => setAlertMessage("")}
												/>
											)}
											{activity?.assignedLessons.length === 0 ? (
												<div className="text-center">
													<h2 className="pt-4 pb-4">
														You have not assigned any lessons yet!
													</h2>
													<h3 className="text-base">
														Click on the Lesson Library tab to view all
														available lessons.
													</h3>
													<div className="relative flex py-2 items-center sm:mx-auto sm:w-full sm:max-w-md">
														<div className="grow border-t border-gray-400"></div>
														<span className="shrink mx-4 text-sm">
															or
														</span>
														<div className="grow border-t border-gray-400"></div>
													</div>
													<h3 className="pb-4 text-base">
														Click on the button below to assign a lesson
														now.
													</h3>
													<AssignLessonButton
														handleOpenAssignLessonDialog={
															handleOpenAssignLessonDialog
														}
													/>
												</div>
											) : (
												<>
													<div className="flex flex-col mt-4 gap-2 w-full md:flex-row md:gap-4">
														<Select
															value={statusFilter}
															setValue={(v) =>
																setStatusFilter(v as string)
															}
															label="Status"
															simpleListBoxId="statusFilter"
															options={[
																{ value: "all", label: "All" },
																{
																	value: "in progress",
																	label: "In Progress",
																},
																{
																	value: "complete",
																	label: "Complete",
																},
															]}
															simpleListBoxClasses="w-full md:w-64"
														/>
														<div className="flex gap-4 items-end justify-between w-full">
															<SearchFilter
																id="searchByTitle"
																name="searchByTitle"
																searchFilter={searchFilter}
																setSearchFilter={setSearchFilter}
																label="Search"
																placeHolder="Search by title..."
																className="w-full md:w-64"
															/>
															<AssignLessonButton
																handleOpenAssignLessonDialog={
																	handleOpenAssignLessonDialog
																}
															/>
														</div>
													</div>
													{filteredLessonActivity.length > 0 ? (
														<LessonActivityTable
															assignments={filteredLessonActivity.filter(
																(row) =>
																	row.name
																		.toLowerCase()
																		.includes(
																			searchFilter
																				.trim()
																				.toLowerCase()
																		)
															)}
															lessonSelected={lessonSelected}
															setAlertMessage={setAlertMessage}
															setLessonSelected={setLessonSelected}
															handleOpenAssignLessonDialog={
																handleOpenAssignLessonDialog
															}></LessonActivityTable>
													) : (
														<table className="tbl tbl-data mt-4">
															<thead>
																<tr>
																	<th className="w-8"></th>
																	<th>Title</th>
																	<th>Category</th>
																	<th>Duration</th>
																	<th className="min-w-64">
																		Status
																	</th>
																	<th className="!pr-3">
																		Description
																	</th>
																	<th className="w-8 !pl-1 !pr-2"></th>
																</tr>
															</thead>
															<NoDataTableBody
																columnCount={7}
																message={`There are no ${statusFilter} lessons.`}
															/>
														</table>
													)}
												</>
											)}
										</section>
									)}
								{isSuccess &&
									currentTab === TeacherLessonsSubTabs.LessonLibrary && (
										<section className="shadow-sm">
											<div className="flex flex-col mt-4 gap-2 w-full flex-wrap lg:flex-nowrap md:gap-x-4 sm:flex-row">
												<Select
													value={categoryFilter}
													setValue={(v) =>
														handleFilterChangeWithPagination(
															setCategoryFilter,
															v as string
														)
													}
													label="Category"
													simpleListBoxId="categoryFilter"
													options={categoryOptions}
													fieldClasses="flex-[1_1_calc(50%-8px)] lg:flex-auto"
													simpleListBoxClasses="w-full lg:w-64"
												/>
												<Select
													value={difficultyFilter}
													setValue={(v) =>
														handleFilterChangeWithPagination(
															setDifficultyFilter,
															v as string
														)
													}
													label="Difficulty Level"
													simpleListBoxId="difficultyFilter"
													options={[
														{ value: "", label: "All Levels" },
														{
															value: "Beginner",
															label: "Beginner",
														},
														{
															value: "Intermediate",
															label: "Intermediate",
														},
														{
															value: "Advanced",
															label: "Advanced",
														},
													]}
													fieldClasses="flex-[1_1_calc(50%-8px)] lg:flex-auto"
													simpleListBoxClasses="w-full lg:w-64"
												/>
												<div className="flex gap-4 items-end justify-between w-full">
													<SearchFilter
														id="searchByTitle"
														name="searchByTitle"
														searchFilter={searchFilter}
														setSearchFilter={(val) => {
															setSearchFilter(val);
															setPage(0);
														}}
														label="Search"
														placeHolder="Search by title..."
														className="w-full min-[1072px]:w-64"
													/>
													<AssignLessonButton
														handleOpenAssignLessonDialog={
															handleOpenAssignLessonDialog
														}
													/>
												</div>
											</div>
											<LessonLibraryTable
												filteredData={getPagedData(
													getFilteredLessonData(lessonData)
												)}
											/>
											<SimplePagination
												page={page}
												pageSize={pageSize}
												totalCount={lessonData.length}
												itemCount={getFilteredLessonData(lessonData).length}
												isFiltered={isLessonDataFiltered}
												onChange={(page: number) => handlePageChange(page)}
												className={"border-none"}
											/>
										</section>
									)}
								{isAssignLessonDialogOpen && (
									<ModalFrame
										open={isAssignLessonDialogOpen}
										onClose={() => setIsAssignLessonDialogOpen(false)}
										title={lockLesson ? "Assign Students" : "Assign Lessons"}>
										<AssignLessonForm
											lockLesson={lockLesson ?? false}
											tutorialId={lessonSelected}
											setAlertMessage={setAlertMessage}
											onConfirm={() => handleLessonAssigned()}
											onClose={() => setIsAssignLessonDialogOpen(false)}
										/>
									</ModalFrame>
								)}
							</section>
						</div>
					</div>
				</div>
			)}

			{view == "raw" && (
				<>
					<RtkqResultViewer heading="Activity" result={actiResult} />
					<RtkqResultViewer heading="Tutorials" result={tutsResult} />
					<RtkqResultViewer heading="Categories" result={catsResult} />
				</>
			)}
		</>
	);
}

interface AssignLessonButtonProps {
	lockLesson?: boolean;
	lessonSelected?: number;
	label?: string;
	handleOpenAssignLessonDialog: (lockLesson: boolean, lessonSelected?: number) => void;
}

function AssignLessonButton(props: AssignLessonButtonProps) {
	return (
		<button
			id="assign-lesson-button"
			className="btn btn-primary min-w-40 md:ml-auto"
			onClick={() =>
				props.handleOpenAssignLessonDialog(props.lockLesson ?? false, props.lessonSelected)
			}>
			<span className="flex items-center">
				<PlusIcon className="size-4 mr-2" /> {props.label ?? "Assign Lesson"}
			</span>
		</button>
	);
}

interface LessonLibraryTableProps {
	filteredData: LessonData[];
}

function LessonLibraryTable({ filteredData }: LessonLibraryTableProps) {
	const containsAssignment = filteredData.some((a) => a.totalCount > 0);

	return (
		<div className="shadow-sm overflow-hidden mt-4">
			<table className="tbl tbl-data">
				<thead>
					<tr>
						<th>Title</th>
						<th>Level</th>
						<th>Duration</th>
						<th className={containsAssignment ? "min-w-64" : ""}>Status</th>
						<th>Description</th>
					</tr>
				</thead>
				{filteredData.length > 0 ? (
					<tbody>
						{filteredData.map((row) => (
							<tr key={row.tutorialId}>
								<td>
									<Link
										id={`library-${row.tutorialId}`}
										to={`/lessons/${row.codeName}`}>
										{row.name}
									</Link>
								</td>
								<td>{row.difficulty}</td>
								<td>
									{Math.round(row.durationSeconds / 60)}
									&nbsp;
									{Math.round(row.durationSeconds / 60) == 1
										? "minute"
										: "minutes"}
								</td>
								<td>
									<StatusColumnTemplate row={row} />
								</td>
								<td>
									<div className="max-w-[1250px] h-12 line-clamp-2 hover:line-clamp-none hover:h-auto">
										{row.description}
									</div>
								</td>
							</tr>
						))}
					</tbody>
				) : (
					<NoDataTableBody
						columnCount={6}
						message="There are no lessons available in this category."
					/>
				)}
			</table>
		</div>
	);
}

interface LessonActivityTableProps {
	assignments: LessonData[];
	lessonSelected?: number;
	handleOpenAssignLessonDialog: (lockLesson: boolean, lessonSelected?: number) => void;
	setAlertMessage: (message: string) => void;
	setLessonSelected: (id?: number) => void;
}

interface LessonActivityTableData {
	tutorialId: number;
	row: LessonData;
}

const mapLessonData = (data: LessonData[]): LessonActivityTableData[] => {
	return data.map((row) => {
		return {
			tutorialId: row.tutorialId,
			row: row,
		};
	});
};

function LessonActivityTable(props: LessonActivityTableProps) {
	const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | DataTableValueArray>();

	const [removeAllAssignments] = useRemoveAllLessonAssignmentsMutation();

	const data = mapLessonData(props.assignments);

	useEffect(() => {
		if (!props.lessonSelected) {
			setExpandedRows({});
		} else {
			expandRow();
		}
	}, [props.lessonSelected]);

	useEffect(() => {
		if (props.lessonSelected) {
			expandRow();
		}
	}, [props.assignments]);

	function expandRow() {
		const tutorialId =
			props.assignments.find((t) => t.tutorialId == props.lessonSelected)?.tutorialId ?? 0;
		setExpandedRows({ [tutorialId]: true });
	}

	const onRowExpand = (event: any) => {
		// clicking on the row expander triggers a nested DataTableRowEvent
		const target =
			event.originalEvent.currentTarget ??
			(event.originalEvent.originalEvent.currentTarget as HTMLElement);

		if (target) {
			const table = target.closest("table");
			table &&
				[...table.getElementsByClassName("expanded")].forEach((e: HTMLElement) =>
					e.classList.remove("expanded")
				);

			const tr = target.closest("tr");
			tr && tr?.classList.add("expanded");
		}
		setExpandedRows({ [event.data.tutorialId]: true });
	};

	const onRowCollapse = (event: any) => {
		// clicking on the row expander triggers a nested DataTableRowEvent
		const target =
			event.originalEvent.currentTarget ??
			(event.originalEvent.originalEvent.currentTarget as HTMLElement);

		if (target) {
			const table = target.closest("table");
			table &&
				[...table.getElementsByClassName("expanded")].forEach((e: HTMLElement) =>
					e.classList.remove("expanded")
				);
		}
		setExpandedRows({});
		props.setLessonSelected(undefined);
	};

	const rowExpansionTemplate = (data: LessonActivityTableData) => {
		return (
			<LessonExpandedTemplate
				assignments={props.assignments}
				lessonSelected={data.tutorialId}
				handleOpenAssignLessonDialog={props.handleOpenAssignLessonDialog}
				setAlertMessage={props.setAlertMessage}></LessonExpandedTemplate>
		);
	};

	const onRowClick = (event: DataTableRowEvent) => {
		const xRows: any = expandedRows;

		if (xRows && xRows[event.data.tutorialId]) {
			onRowCollapse(event);
		} else {
			onRowExpand(event);
		}
	};

	const titleBodyTemplate = (data: LessonActivityTableData) => {
		return (
			<Link id={`activity-${data.row.tutorialId}`} to={`/lessons/${data.row.codeName}`}>
				{data.row.name}
			</Link>
		);
	};

	const categoryBodyTemplate = (data: LessonActivityTableData) => {
		return (
			<span className="whitespace-nowrap">
				<span>{data.row.categoryName}</span>
				<div className="mt-1 text-gray-500 text-sm">{data.row.difficulty}</div>
			</span>
		);
	};

	const durationBodyTemplate = (data: LessonActivityTableData) => {
		return (
			<span>
				{Math.round(data.row.durationSeconds / 60)}
				&nbsp;{Math.round(data.row.durationSeconds / 60) == 1 ? "minute" : "minutes"}
			</span>
		);
	};

	const statusBodyTemplate = (data: LessonActivityTableData) => {
		return (
			<div>
				<StatusColumnTemplate row={data.row} />
			</div>
		);
	};

	const descriptionBodyTemplate = (data: LessonActivityTableData) => {
		return (
			<div className="max-w-[1250px] h-12 line-clamp-2 hover:line-clamp-none hover:h-auto ">
				{data.row.description}
			</div>
		);
	};

	const deleteBodyTemplate = (data: LessonActivityTableData) => {
		return (
			<ButtonWithConfirm
				id={`delete-${data.tutorialId}-button`}
				className="mr-2"
				message={`Are you sure you want to delete the assignment "${data.row.name}" for all students? Student lesson activity will be preserved.`}
				type={AlertLevel.Warning}
				confirmLabel="Yes"
				cancelLabel="No"
				onConfirm={() => handleRemoveAllAssignments(data.tutorialId)}>
				<TrashIcon className="size-5" />
			</ButtonWithConfirm>
		);
	};

	function handleRemoveAllAssignments(tutorialId: number) {
		removeAllAssignments([tutorialId])
			.unwrap()
			.then((messages: string[]) => {
				let message = "";
				messages.map((m) => (message += `${m} \n`));
				props.setAlertMessage(message);
			})
			.catch((error) => console.log(error));
	}

	return (
		<DataTable
			value={data}
			dataKey="tutorialId"
			stripedRows
			expandedRows={expandedRows}
			onRowClick={onRowClick}
			onRowExpand={onRowExpand}
			onRowCollapse={onRowCollapse}
			rowExpansionTemplate={rowExpansionTemplate}
			selectionMode="single"
			emptyMessage="No results found"
			className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
			style={{ paddingTop: "16px" }}>
			<Column expander={true} className="w-8"></Column>
			<Column header="Title" body={titleBodyTemplate}></Column>
			<Column header="Category" body={categoryBodyTemplate}></Column>
			<Column header="Duration" body={durationBodyTemplate}></Column>
			<Column header="Status" body={statusBodyTemplate} className="min-w-64"></Column>
			<Column header="Description" body={descriptionBodyTemplate} className="!pr-3"></Column>
			<Column body={deleteBodyTemplate} className="w-8 !pl-1 !pr-2"></Column>
		</DataTable>
	);
}

interface LessonExpandedProps {
	assignments: LessonData[];
	lessonSelected: number;
	setAlertMessage: (message: string) => void;
	handleOpenAssignLessonDialog: (lockLesson: boolean, lessonSelected?: number) => void;
}

function LessonExpandedTemplate(props: LessonExpandedProps) {
	const lessonData = props.assignments.find(
		(t) => t.tutorialId === props.lessonSelected
	)?.assignments;

	const [statusFilter, setStatusFilter] = useState("all");
	const [filteredData, setFilteredData] = useState(lessonData);

	const [allAssignmentsLength, setAllAssignmentsLength] = useState(0);
	const [checkedIds, setCheckedIds] = useState<number[]>([]);
	const [allChecked, setAllChecked] = useState(false);
	const [allFilteredCheckboxIds, setAllFilteredCheckboxIds] = useState<number[]>([]);
	const [allFilteredChecked, setAllFilteredChecked] = useState(false);

	const [removeLessonAssignment] = useRemoveLessonAssignmentMutation();

	useEffect(() => {
		if (checkedIds.length === allAssignmentsLength && allAssignmentsLength != 0) {
			setAllChecked(true);
		} else {
			setAllChecked(false);
		}

		updateSelectAllCheckbox();
	}, [checkedIds]);

	function updateSelectAllCheckbox() {
		if (allFilteredCheckboxIds.every((id) => checkedIds.includes(id))) {
			setAllFilteredChecked(true);
		} else {
			setAllFilteredChecked(false);
		}
	}

	useEffect(() => {
		updateSelectAllCheckbox();
	}, [allFilteredCheckboxIds]);

	useEffect(() => {
		const _temp: number[] = [];

		lessonData?.map((row) => {
			_temp.push(Number(row.userId));
		});
		setAllFilteredCheckboxIds(_temp);
		setAllAssignmentsLength(lessonData?.length ?? 0);
	}, [lessonData]);

	useEffect(() => {
		if (lessonData) {
			if (statusFilter === "in progress")
				setFilteredData(lessonData.filter((a) => a.timeStarted && !a.completed));
			else if (statusFilter === "complete")
				setFilteredData(lessonData.filter((a) => a.completed));
			else if (statusFilter === "not started")
				setFilteredData(lessonData.filter((a) => !a.timeStarted));
			else setFilteredData(lessonData);
		}
	}, [statusFilter, lessonData]);

	useEffect(() => {
		setAllFilteredCheckboxIds(filteredData?.map((row) => row.userId) ?? []);
	}, [filteredData]);

	function handleCheckboxChange(e: ChangeEvent<HTMLInputElement>) {
		if (e.target.checked) {
			setCheckedIds([...checkedIds, Number(e.target.value)]);
		} else {
			setCheckedIds(checkedIds.filter((item) => item !== Number(e.target.value)));
		}
	}

	function handleSelectAll(e: ChangeEvent<HTMLInputElement>) {
		if (e.target.checked) {
			const idsToAdd: number[] = [];
			allFilteredCheckboxIds.map((id) => {
				if (!checkedIds.includes(id)) {
					idsToAdd.push(id);
				}
			});
			setCheckedIds([...checkedIds, ...idsToAdd]);
		} else {
			setCheckedIds(checkedIds.filter((item) => !allFilteredCheckboxIds.includes(item)));
		}
	}

	function handleRemoveAssignments() {
		removeLessonAssignment({ tutorialId: props.lessonSelected, studentIds: checkedIds })
			.unwrap()
			.then((message) => {
				props.setAlertMessage(message);
				setCheckedIds([]);
			})
			.catch((error) => console.log(error));
	}

	return (
		<div className="-mx-4 -my-3 px-4 pb-3 bg-gray-100">
			<div className="mx-8 pt-1 flex flex-row relative justify-between items-end">
				<Select
					value={statusFilter}
					setValue={(v) => setStatusFilter(v as string)}
					label="Status"
					simpleListBoxId="statusFilter"
					options={[
						{ value: "all", label: "All" },
						{
							value: "not started",
							label: "Not Started",
						},
						{
							value: "in progress",
							label: "In Progress",
						},
						{ value: "complete", label: "Complete" },
					]}
					simpleListBoxClasses="w-64"
				/>
				<div className="flex space-x-2">
					<AssignLessonButton
						lockLesson={true}
						lessonSelected={props.lessonSelected}
						label="Add Students"
						handleOpenAssignLessonDialog={props.handleOpenAssignLessonDialog}
					/>
					<ButtonWithConfirm
						id={`remove-${props.lessonSelected}-button`}
						disabled={checkedIds.length === 0}
						className="btn btn-neutral"
						message={`Are you sure you want to remove ${checkedIds.length} student(s) from the assignment?`}
						type={AlertLevel.Warning}
						confirmLabel="Yes"
						cancelLabel="No"
						onConfirm={handleRemoveAssignments}>
						<span className="flex items-center">
							<XCircleIcon className="size-4 mr-2" /> Remove
						</span>
					</ButtonWithConfirm>
				</div>
			</div>
			<div className="mt-2 mx-8">
				<table className="tbl table-fixed tbl-data">
					<thead>
						<tr>
							<th className="w-8">
								<input
									id="selectAllCheckbox"
									type="checkbox"
									className="size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
									onChange={(e) => handleSelectAll(e)}
									checked={allChecked || allFilteredChecked}
								/>
							</th>
							<th className="w-16"></th>
							<th>Name</th>
							<th className="w-8"></th>
							<th>Time Spent</th>
							<th>Date Completed</th>
						</tr>
					</thead>

					{filteredData?.length == 0 ? (
						<NoDataTableBody columnCount={6} message="No matching records found" />
					) : (
						<tbody>
							{filteredData?.map((row) => {
								return (
									<tr key={"student-" + row.userId}>
										<td className="!py-1.5">
											<input
												id={"student-" + row.userId}
												value={row.userId}
												type="checkbox"
												checked={checkedIds.includes(row.userId)}
												className="size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
												onChange={(e) => handleCheckboxChange(e)}
											/>
										</td>
										<td className="!py-1.5">
											<Avatar
												initials={row.initials}
												className="!ml-3 !size-8"
												hasImage={row.hasImage}
												userId={row.userId}
											/>
										</td>
										<td className="!py-1.5">
											<TextWithTip
												uniqueName={`name-${row.userId}`}
												text={row.formalName}
											/>
										</td>
										<td className="!py-1.5">
											{row.completed ? (
												<CheckBadgeIconOutline
													className="size-5 text-green-700"
													aria-hidden="true"
													data-tooltip-id="completed-tooltip"
												/>
											) : row.timeStarted ? (
												<ClockIcon
													className="size-5 text-yellow-500"
													aria-hidden="true"
													data-tooltip-id="in-progress-tooltip"
												/>
											) : (
												<ExclamationTriangleIcon
													className="size-5 text-red-500"
													aria-hidden="true"
													data-tooltip-id="not-started-tooltip"
												/>
											)}
											<Tooltip
												id="completed-tooltip"
												place="top"
												content="Completed"
											/>
											<Tooltip
												id="in-progress-tooltip"
												place="top"
												content="In Progress"
											/>
											<Tooltip
												id="not-started-tooltip"
												place="top"
												content="Not Started"
											/>
										</td>
										<td className="!py-1.5">
											{Math.round(row.timeSpentSeconds / 60)}
											&nbsp;
											{Math.round(row.timeSpentSeconds / 60) == 1
												? "minute"
												: "minutes"}
										</td>
										<td className="!py-1.5">
											{row.timeStarted && row.completed ? (
												<FormattedDate date={row.timeStarted} />
											) : (
												"-"
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					)}
				</table>
			</div>
		</div>
	);
}

interface StatusColumnTemplateProps {
	row: LessonData;
}

function StatusColumnTemplate({ row }: StatusColumnTemplateProps) {
	return row.totalCount === 0 ? (
		<></>
	) : row.status == LessonStatus.Complete ? (
		<div className="items-center text-green-700 inline-flex float-right">
			<CheckBadgeIcon className="mr-1.5 h-7 w-7" aria-hidden="true" />
			<p>
				<strong>
					{row.totalCount}
					<span className="text-xs">/{row.totalCount}</span>
				</strong>{" "}
				complete!
			</p>
		</div>
	) : row.incompleteCount == row.totalCount ? (
		<div>
			<div className="w-full">
				<ProgressBar
					id={row.tutorialId}
					started={row.startedCount}
					showStartedOnly={true}
					assigned={row.totalCount}
					barColor="bg-zinc-400"
				/>
				<p className="text-gray-500 text-sm text-right pt-0.5">
					<strong>
						{row.startedCount}
						<span className="text-xs">/{row.totalCount}</span>
					</strong>{" "}
					students have started
				</p>
			</div>
		</div>
	) : (
		<div>
			<div className="w-full">
				<ProgressBar
					id={row.tutorialId}
					started={row.startedCount}
					completed={row.totalCount - row.incompleteCount}
					assigned={row.totalCount}
					completedLabel="Completed"
				/>
				<p className="text-gray-500 text-sm text-right pt-0.5">
					<strong>
						{row.totalCount - row.incompleteCount}
						<span className="text-xs">/{row.totalCount}</span>
					</strong>{" "}
					students have completed
				</p>
			</div>
		</div>
	);
}
